import { useState, useMemo, useEffect, useRef } from "react";
import { playSequence, stopAllAudio, kickAudio, onAudioStop } from "@/audio/piano";
import { spellScale, spellDegree, toAscii, parseNote } from "@/theory/spell";

/**
 * ScaleExplorer — a horizontal scale strip with click-to-hear notes.
 *
 * Shows the scale's seven notes as numbered tiles, one row per scale type.
 * The "Tonic" tile pulses on the chosen root, intervals are colour-coded
 * (whole-step = neutral, half-step = orange flag, augmented-2 = red flag).
 *
 * Used in lessons 1, 3, 4, 12, 16, 23, 24, 25, 27, 32 — every scale-shape
 * lesson in the course.
 */

// Scale formulas: each entry is the interval (in semitones) from the tonic.
export const SCALE_FORMULAS: Record<string, { name: string; intervals: number[]; degrees: string[] }> = {
  major:           { name: "Major",                intervals: [0, 2, 4, 5, 7, 9, 11],   degrees: ["1", "2", "3", "4", "5", "6", "7"] },
  naturalMinor:    { name: "Natural Minor",        intervals: [0, 2, 3, 5, 7, 8, 10],   degrees: ["1", "2", "♭3", "4", "5", "♭6", "♭7"] },
  harmonicMinor:   { name: "Harmonic Minor",       intervals: [0, 2, 3, 5, 7, 8, 11],   degrees: ["1", "2", "♭3", "4", "5", "♭6", "7"] },
  melodicMinor:    { name: "Melodic Minor (jazz)", intervals: [0, 2, 3, 5, 7, 9, 11],   degrees: ["1", "2", "♭3", "4", "5", "6", "7"] },
  dorian:          { name: "Dorian",               intervals: [0, 2, 3, 5, 7, 9, 10],   degrees: ["1", "2", "♭3", "4", "5", "6", "♭7"] },
  phrygian:        { name: "Phrygian",             intervals: [0, 1, 3, 5, 7, 8, 10],   degrees: ["1", "♭2", "♭3", "4", "5", "♭6", "♭7"] },
  lydian:          { name: "Lydian",               intervals: [0, 2, 4, 6, 7, 9, 11],   degrees: ["1", "2", "3", "♯4", "5", "6", "7"] },
  mixolydian:      { name: "Mixolydian",           intervals: [0, 2, 4, 5, 7, 9, 10],   degrees: ["1", "2", "3", "4", "5", "6", "♭7"] },
  locrian:         { name: "Locrian",              intervals: [0, 1, 3, 5, 6, 8, 10],   degrees: ["1", "♭2", "♭3", "4", "♭5", "♭6", "♭7"] },
  majorPentatonic: { name: "Major Pentatonic",     intervals: [0, 2, 4, 7, 9],          degrees: ["1", "2", "3", "5", "6"] },
  minorPentatonic: { name: "Minor Pentatonic",     intervals: [0, 3, 5, 7, 10],         degrees: ["1", "♭3", "4", "5", "♭7"] },
  blues:           { name: "Blues",                intervals: [0, 3, 5, 6, 7, 10],      degrees: ["1", "♭3", "4", "♭5", "5", "♭7"] },
  phrygianDominant:{ name: "Phrygian Dominant",    intervals: [0, 1, 4, 5, 7, 8, 10],   degrees: ["1", "♭2", "3", "4", "5", "♭6", "♭7"] },
  hungarianMinor:  { name: "Hungarian Minor",      intervals: [0, 2, 3, 6, 7, 8, 11],   degrees: ["1", "2", "♭3", "♯4", "5", "♭6", "7"] },
};

type ScaleKey = keyof typeof SCALE_FORMULAS;

interface ScaleExplorerProps {
  /** Tonic note name without octave — e.g. "C", "F#", "Bb". */
  tonic?: string;
  /** Octave to play the tonic in (default 4). */
  octave?: number;
  /** Which scales to display (default: just "major"). */
  scales?: ScaleKey[];
  /** Use flat names instead of sharps (default: smart-pick). */
  preferFlats?: boolean;
}

const TONIC_OPTIONS = ["C", "G", "D", "A", "E", "B", "F", "Bb", "Eb", "Ab", "Db", "Gb"];

export function ScaleExplorer({
  tonic: initialTonic = "C",
  octave = 4,
  scales: initialScales = ["major"],
  preferFlats,
}: ScaleExplorerProps) {
  const [tonic, setTonic] = useState(initialTonic);
  const [scales, setScales] = useState<ScaleKey[]>(initialScales);

  // `preferFlats` is retained for callers that force a spelling, but names now
  // come from the DEGREE labels each formula carries — so C minor's third is
  // spelled E♭ (the letter after D must be E) and never D♯.
  void preferFlats;
  const tonicSemi = useMemo(() => parseNote(tonic)?.pc ?? 0, [tonic]);

  // Names for one scale, correctly spelled: ["C","D","E♭","F","G","A♭","B♭"].
  const namesFor = (scaleKey: ScaleKey) =>
    spellScale(tonic, SCALE_FORMULAS[scaleKey].degrees);

  const [playingKey, setPlayingKey] = useState<ScaleKey | null>(null);
  const stopRef = useRef<(() => void) | null>(null);

  const stopScale = () => {
    stopRef.current?.();
    stopRef.current = null;
    setPlayingKey(null);
  };
  // Another widget started something: our sound is gone, so reflect it.
  useEffect(() => onAudioStop(() => { stopRef.current = null; setPlayingKey(null); }), []);
  useEffect(() => () => stopRef.current?.(), []);

  /** One note, on the audio clock. Interrupts whatever was sounding. */
  const playNote = (semi: number, name: string, oct = octave) => {
    kickAudio();
    stopScale();
    stopAllAudio();
    const pitch = `${toAscii(name)}${oct + Math.floor(semi / 12)}`;
    void playSequence([{ time: 0, notes: [pitch], duration: "8n", velocity: 0.75 }], 120)
      .then((stop) => { stopRef.current = stop; });
  };

  /**
   * Play the scale on the Tone.js transport rather than a chain of setTimeouts,
   * so it stays in time and — more importantly — can actually be stopped.
   */
  const playScale = (scaleKey: ScaleKey) => {
    kickAudio();
    if (playingKey === scaleKey) { stopScale(); stopAllAudio(); return; }
    const f = SCALE_FORMULAS[scaleKey];
    const names = namesFor(scaleKey);
    const step = 0.24; // seconds per note
    const events = f.intervals.map((iv, i) => ({
      time: i * step,
      notes: [`${toAscii(names[i])}${octave + Math.floor((tonicSemi + iv) / 12)}`],
      duration: "8n",
      velocity: 0.75,
    }));
    // …and the octave on top.
    events.push({
      time: f.intervals.length * step,
      notes: [`${toAscii(spellDegree(tonic, "1") ?? tonic)}${octave + Math.floor((tonicSemi + 12) / 12)}`],
      duration: "8n",
      velocity: 0.75,
    });
    setPlayingKey(scaleKey);
    void playSequence(events, 120).then((stop) => {
      stopRef.current = stop;
      // playSequence runs the global stop bus on its way in, and our own
      // listener clears this flag — so set it again now that we're running.
      setPlayingKey(scaleKey);
    });
  };

  return (
    <div className="my-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.03] to-purple-500/[0.03] p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300">
            Scale explorer
          </div>
          <div className="mt-1 font-mt text-xl text-nsm-ink" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Tonic: <span className="font-semibold text-amber-300">{tonic}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {TONIC_OPTIONS.map((t) => (
            <button
              key={t}
              onClick={() => setTonic(t)}
              className={`min-w-[32px] rounded px-2 py-1.5 text-[12px] font-semibold transition ${
                t === tonic
                  ? "bg-amber-500/30 text-amber-100"
                  : "bg-zinc-800/50 text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {scales.map((scaleKey) => {
          const f = SCALE_FORMULAS[scaleKey];
          const names = namesFor(scaleKey);
          return (
            <div key={scaleKey} className="rounded-xl border border-nsm-border bg-zinc-900/40 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[12px] uppercase tracking-wider text-nsm-dim">
                  {f.name}
                </div>
                <button
                  onClick={() => playScale(scaleKey)}
                  className="rounded-full bg-amber-500/20 px-3.5 py-1.5 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/30"
                >
                  {playingKey === scaleKey ? "■ stop" : "▶ play"}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {f.intervals.map((iv, i) => {
                  const noteName = names[i];
                  const isTonic = i === 0;
                  // Gap to the next note. Three semitones is only an AUGMENTED
                  // 2nd when the two notes sit on adjacent letters (harmonic
                  // minor's A♭→B). Across a letter skip — the blues and minor
                  // pentatonic 1→♭3 — it is a plain minor 3rd.
                  const nextIv = f.intervals[i + 1] ?? 12;
                  const gap = nextIv - iv;
                  const nextName = names[i + 1] ?? spellDegree(tonic, "1") ?? "";
                  const letterStep =
                    nextName && noteName
                      ? (("CDEFGAB".indexOf(nextName[0]) - "CDEFGAB".indexOf(noteName[0])) + 7) % 7
                      : 1;
                  const flag =
                    gap === 1 ? "H"
                    : gap === 2 ? "W"
                    : gap === 3 ? (letterStep === 1 ? "A2" : "m3")
                    : "";
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <button
                        onClick={() => playNote(tonicSemi + iv, noteName)}
                        className={`flex h-12 w-14 flex-col items-center justify-center rounded-lg border transition ${
                          isTonic
                            ? "border-amber-400 bg-amber-500/20 text-amber-100 shadow-[0_0_0_3px_rgba(245,158,11,0.15)]"
                            : "border-nsm-border bg-zinc-800/40 text-nsm-ink hover:border-amber-500/40 hover:bg-zinc-800"
                        }`}
                      >
                        <span className="font-mt text-base font-semibold leading-none">
                          {noteName}
                        </span>
                        <span className="text-[9px] text-nsm-dim">{f.degrees[i]}</span>
                      </button>
                      {flag && (
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wider ${
                            flag === "H" ? "text-orange-400" :
                            flag === "A2" ? "text-rose-400" :
                            flag === "m3" ? "text-sky-400" : "text-zinc-600"
                          }`}
                          title={
                            flag === "H" ? "Half-step"
                            : flag === "A2" ? "Augmented 2nd"
                            : flag === "m3" ? "Minor 3rd"
                            : "Whole-step"
                          }
                        >
                          {flag}
                        </span>
                      )}
                    </div>
                  );
                })}
                {/* Octave tile */}
                <div className="flex h-12 w-14 flex-col items-center justify-center rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-200">
                  <span className="font-mt text-base font-semibold leading-none">
                    {spellDegree(tonic, "1")}
                  </span>
                  <span className="text-[9px] text-amber-300/70">8</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Picker for additional scales */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="mr-1 uppercase tracking-wider text-nsm-dim">Add scale:</span>
        {(Object.keys(SCALE_FORMULAS) as ScaleKey[])
          .filter((k) => !scales.includes(k))
          .map((k) => (
            <button
              key={k}
              onClick={() => setScales([...scales, k])}
              className="rounded-full border border-nsm-border bg-zinc-800/40 px-2 py-0.5 text-nsm-dim hover:border-amber-500/40 hover:text-nsm-ink"
            >
              + {SCALE_FORMULAS[k].name}
            </button>
          ))}
        {scales.length > 1 && (
          <button
            onClick={() => setScales([scales[0]])}
            className="ml-auto inline-flex min-h-[32px] items-center px-2 text-[12px] text-amber-300 underline hover:text-amber-200"
          >
            ↺ reset
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] text-nsm-dim">
        <span><strong className="text-orange-400">H</strong> = half-step (1 semitone)</span>
        <span><strong className="text-zinc-500">W</strong> = whole-step (2 semitones)</span>
        <span><strong className="text-rose-400">A2</strong> = augmented 2nd (3 semitones, adjacent letters)</span>
        <span><strong className="text-sky-400">m3</strong> = minor 3rd (3 semitones, letter skipped)</span>
      </div>
    </div>
  );
}
